import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AUTH0_CONFIG } from '@athena/data-access-auth0';
import { petDtoToScrapedPet } from '../pet/pet-dto.adapter';
import { PagedResultDTO } from '../pet/pet.model';
import { MatchesQueryParams, PagedMatches } from './match.model';
import { MatchedPetDTO } from './matches.dto';

@Injectable({ providedIn: 'root' })
export class MatchesApiService {
  readonly #http = inject(HttpClient);
  readonly #config = inject(AUTH0_CONFIG);

  getMatches(query: MatchesQueryParams = {}): Observable<PagedMatches> {
    let params = new HttpParams();
    if (query.page !== undefined) params = params.set('page', String(query.page));
    if (query.pageSize !== undefined) params = params.set('pageSize', String(query.pageSize));
    if (query.maxMatches !== undefined) params = params.set('maxMatches', String(query.maxMatches));
    if (query.includeDisqualified) params = params.set('includeDisqualified', 'true');
    if (query.includeDismissed) params = params.set('includeDismissed', 'true');

    return this.#http
      .get<PagedResultDTO<MatchedPetDTO>>(`${this.#config.apiBaseUrl}/users/me/matches`, {
        params,
        withCredentials: true,
      })
      .pipe(map((dto) => this.toDomain(dto)));
  }

  // Mark a pet as "not interested". Idempotent per AB#1840 D4: 204 on
  // success for any valid listed petId; 404 only when the id is malformed,
  // unknown, or no longer listed. D8: dismissing a favourited pet also
  // removes the favourite server-side — callers must drop the local
  // favourite state after this resolves.
  dismiss(petId: string): Observable<void> {
    return this.#http.put<void>(`${this.#config.apiBaseUrl}/users/me/matches/not-interested/${petId}`, {}, { withCredentials: true });
  }

  // Remove the "not interested" flag. AB#1840 D4: always 204 — the backend
  // doesn't 404 on an unknown or already-undismissed petId, so this is safe
  // to fire optimistically.
  undismiss(petId: string): Observable<void> {
    return this.#http.delete<void>(`${this.#config.apiBaseUrl}/users/me/matches/not-interested/${petId}`, { withCredentials: true });
  }

  private toDomain(dto: PagedResultDTO<MatchedPetDTO>): PagedMatches {
    return {
      page: dto.page,
      pageSize: dto.pageSize,
      total: dto.total,
      unfilteredTotal: dto.unfilteredTotal,
      data: (dto.data ?? []).map((match) => ({
        pet: petDtoToScrapedPet(match.pet),
        score: match.score,
        quality: match.quality,
        scores: match.scores,
        disqualified: match.disqualified,
        dismissed: match.dismissed ?? false,
        annotations: match.annotations,
      })),
    };
  }
}
